import { faComment, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import { faSearchPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import { photoDetailUrl } from "@/mk2/apps/blogs/utils";
import { ImgResponsive } from "@/mk2/components/ImgResponsive";
import { Link } from "@/mk2/components/Link";
import { BlogPostEntity, ImageEntity, PhotoEntity } from "@/mk2/schemas";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./BlogPostPhotos.mscss";

const MAX_WIDTH = 604;
const MAX_HEIGHT = 480;

const showMagnifier = (photo: PhotoEntity) => photo.versions.includes("s1600x1600");

interface ResizedPhotoOwnProps {
    className?: string;
    forceEager: boolean;
    maxHeight: number;
    maxWidth: number;
    photo: PhotoEntity;
    enableResponsiveLayoutForSmallImages: boolean;
    enableResponsiveLayoutForLargeImages: boolean;
    version?: string;
}

export const ResizedPhoto = ({
    className,
    forceEager,
    maxHeight,
    maxWidth,
    photo,
    enableResponsiveLayoutForSmallImages,
    enableResponsiveLayoutForLargeImages,
    version = "s720x720",
}: ResizedPhotoOwnProps) => {
    const wscale = photo.width / maxWidth;
    const hscale = photo.height / maxHeight;
    const cropAxis = hscale >= wscale ? "width" : "height";

    let imgWidth;
    let imgHeight;
    if (cropAxis === "height") {
        imgWidth = Math.min(photo.width, maxWidth);
        const scale = photo.width / imgWidth;
        imgHeight = Math.round(photo.height / scale);
    } else {
        imgHeight = Math.min(photo.height, maxHeight);
        const scale = photo.height / imgHeight;
        imgWidth = Math.round(photo.width / scale);
    }

    const useResponsiveLayout =
        imgWidth >= 320
            ? enableResponsiveLayoutForLargeImages
            : enableResponsiveLayoutForSmallImages;

    return (
        <div
            className={cx(
                className,
                useResponsiveLayout && styles.BlogPostPhotos__responsiveLayout,
            )}
            style={{
                width: useResponsiveLayout ? photo.width : imgWidth,
                height: useResponsiveLayout ? "auto" : imgHeight,
            }}
        >
            <ImgResponsive photo={photo} photoVersion={version} lazy={!forceEager} />
        </div>
    );
};

const CompactPhoto = ({
    complaintsCount,
    forceEager,
    image,
    isMobile,
    post,
}: {
    complaintsCount: number;
    forceEager: boolean;
    image: ImageEntity;
    isMobile: boolean;
    post: BlogPostEntity;
}) => {
    return (
        <Link className={styles.BlogPostPhotos__compactPhoto} to={photoDetailUrl(post, image)}>
            <ResizedPhoto
                className={styles.BlogPostPhotos__compactPhoto__img}
                maxHeight={140}
                maxWidth={500}
                photo={image.photo}
                enableResponsiveLayoutForSmallImages={false}
                enableResponsiveLayoutForLargeImages={false}
                forceEager={forceEager}
            />

            {complaintsCount > 0 && (
                <div
                    className={cx(
                        styles.BlogPostPhotos__info,
                        styles["BlogPostPhotos__info--complaints"],
                    )}
                >
                    <span className={styles.BlogPostPhotos__info__comments}>
                        {image.commentsCount} <FontAwesomeIcon icon={faComment} />
                    </span>
                </div>
            )}
        </Link>
    );
};

const NonCompactPhoto = ({
    forceEager,
    image,
    inFeed,
    isMobile,
    post,
}: {
    forceEager: boolean;
    image: ImageEntity;
    inFeed: boolean;
    isMobile: boolean;
    post: BlogPostEntity;
}) => {
    return (
        <div id={`bp_image_${image.id}`} className={styles.BlogPostPhotos__nonCompactPhoto}>
            <Link to={photoDetailUrl(post, image)}>
                <ResizedPhoto
                    className={styles.BlogPostPhotos__nonCompactPhoto__img}
                    maxHeight={MAX_HEIGHT}
                    maxWidth={MAX_WIDTH}
                    photo={image.photo}
                    enableResponsiveLayoutForSmallImages={isMobile} // pre prípad menších obrázkov
                    enableResponsiveLayoutForLargeImages={isMobile} // pre veľké obrázky
                    forceEager={forceEager}
                />
            </Link>

            {!inFeed && image.likesCount + image.commentsCount > 0 && (
                <div
                    className={cx(
                        styles.BlogPostPhotos__info,
                        styles["BlogPostPhotos__info--no-complaints"],
                    )}
                >
                    {image.likesCount ? (
                        <span className={styles.BlogPostPhotos__info__likes}>
                            {image.likesCount} <FontAwesomeIcon icon={faThumbsUp} />
                        </span>
                    ) : null}
                    {image.commentsCount ? (
                        <span className={styles.BlogPostPhotos__info__comments}>
                            {image.commentsCount} <FontAwesomeIcon icon={faComment} />
                        </span>
                    ) : null}
                </div>
            )}

            {!inFeed && showMagnifier(image.photo) && (
                <div className={styles.BlogPostPhotos__controls}>
                    <a
                        data-google-interstitial="false"
                        href={photoUrl(image.photo, "s1600x1600")}
                        target="_blank"
                        rel="noopener"
                        className={styles.BlogPostPhotos__controls__control}
                    >
                        <FontAwesomeIcon icon={faSearchPlus} />
                    </a>
                </div>
            )}
        </div>
    );
};

interface OwnProps {
    complaintsCounts: { [imageId: number]: number };
    inFeed: boolean;
    isMobile: boolean;
    post: BlogPostEntity;
    showStrip: boolean;
    imagesSubset?: ImageEntity[];
    forceEagerImages: boolean;
    version?: string;
}

export default class BlogPostPhotos extends React.Component<OwnProps> {
    public render() {
        const {
            complaintsCounts,
            inFeed,
            isMobile,
            post,
            imagesSubset,
            showStrip,
            forceEagerImages,
        } = this.props;

        const images = imagesSubset || post.images;

        if (images.length === 0) {
            return null;
        }

        const compact = inFeed && images.length > 1;

        return (
            <div className={styles.BlogPostPhotos}>
                {compact ? (
                    <div
                        className={
                            showStrip
                                ? styles.BlogPostPhotos__strip
                                : styles.BlogPostPhotos__nonstrip
                        }
                    >
                        {images.map((image, idx) => (
                            <CompactPhoto
                                key={image.id}
                                complaintsCount={complaintsCounts[image.id] || 0}
                                image={image}
                                forceEager={idx < 5 ? forceEagerImages : undefined}
                                isMobile={isMobile}
                                post={post}
                            />
                        ))}

                        {showStrip && <div className={styles.BlogPostPhotos__strip__mask} />}
                    </div>
                ) : (
                    images.map((image) => (
                        <NonCompactPhoto
                            key={image.id}
                            image={image}
                            forceEager={forceEagerImages}
                            inFeed={inFeed}
                            isMobile={isMobile}
                            post={post}
                        />
                    ))
                )}
            </div>
        );
    }
}
